import h from "trader-hyperscript";
import {
  Field,
  Form,
  constantField,
  numberBox,
  numberBoxOptional,
  selectBox,
  textBox,
} from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import { country } from "../domain/config";
import {
  renderCurrency,
  renderFormulaTypeShort,
  showFormulaChargeMode,
  showInvoicelineType,
} from "../render";
import * as FA from "./../../styles/fontawesome.css.classes";
import * as style from "./../../styles/style.css.classes";
import {
  invoicelinetype,
  singleDueDateCalculation,
} from "./../parsing_from_ur";
import { makeArrayField } from "./arrayfield";
import currency from "currency.js";
import { wrapField } from "./questionsField";
import { absCurrency, negateCurrency } from "../lib/currency";
import { formulachargemode } from "../../sql/classy/types";
import { helpIcon } from "./helpicon";
import { stringToJsxClientside } from "../pages/editemailtemplates/client";
import { dyn } from "trader.ts/ui";
import { Source } from "trader.ts/types/source";
import { renderPopupModal } from "./popup";
import { renderClosePopupButton } from "../pages/planning/selectionandforms";
import { makeGradient, orange } from "../lib/colors";

type IL<T> = {
  description: string;
  vat: number;
  amount_with_vat: T;
  amount_without_vat: T | null;
  type_: invoicelinetype;
  chargemode: formulachargemode;
};

export async function formulaInvoicelinesForm<PRICE extends currency>(
  initial: IL<PRICE>[],
  opts: {
    allowedTypes?: invoicelinetype[];
    allowedChargemodes?: formulachargemode[];
    addToMainLabel?: string;
    country: country;
    small: boolean;
    hideEditInPopup?: boolean;
  },
): Promise<Field<IL<PRICE>[]>> {
  const renderSmall = opts.small;

  const arrayF = await makeArrayField({
    initialVal: initial,
    makeSubForm: async (t) => singleLineForm(t, opts),
    makeNew: () => ({
      amount_with_vat: currency(0) as PRICE,
      amount_without_vat: null,
      description: "",
      vat: 21,
      type_: "Normal" as const,
      chargemode: "Enrollment" as const,
    }),
    render: (arr, addNew, remove, _moveUp, _moveDown) => (
      <article>
        <label style="margin-bottom: -12px; display: block;">
          {"Factuurlijnen"} {opts?.addToMainLabel || ""}
        </label>
        <table
          className={style.table_has_inputs}
          style="table-layout: fixed; margin-bottom: 4px"
        >
          {renderHeader()}
          {arr.map((a, i) => (
            <tr>
              {a.render()}
              <td>
                <button
                  className={style.button_has_only_icon + style.button_em_down}
                  style={
                    "height: var(--input-height);" + renderSmall
                      ? "padding: 12px;"
                      : ""
                  }
                  onclick={() => remove(i)}
                >
                  <i className={FA.icon_trash_alt_solid}></i>
                </button>
              </td>
            </tr>
          ))}
        </table>
        <button
          onclick={addNew}
          className={style.button_em_down + style.text_with_icon}
          style="width: 100%; height: 32px; padding: 0px; display: block"
        >
          <i className={FA.icon_plus_solid}></i>
          {"Voeg factuurlijn toe"}
        </button>
      </article>
    ),
  });

  if (opts.hideEditInPopup === true) {
    const showingPopupS = new Source(false);
    return {
      s: arrayF.s,
      cleanup: arrayF.cleanup,
      render: () => {
        return (
          <div>
            <label>{"Factuurlijnen"}</label>
            {dyn(arrayF.s, (val) => {
              if (val.tag === "parsed") {
                return (
                  <div>
                    <div>
                      {val.parsed.length === 0 ? (
                        <div
                          className={style.input_like}
                          style="justify-content: flex-start; margin-bottom: 2px;"
                        >
                          <i className={FA.icon_euro_sign_solid_1}></i>
                          <span style="flex: 1">
                            {"Geen factuurlijnen"}
                          </span>
                        </div>
                      ) : (
                        []
                      )}
                      {val.parsed.map((il) => (
                        <div
                          className={style.input_like}
                          style="justify-content: flex-start; margin-bottom: 2px;"
                        >
                          <i className={FA.icon_euro_sign_solid_1}></i>
                          <span style="flex: 1">
                            {(il.type_ === "Normal"
                              ? ""
                              : showInvoicelineType(il.type_) + ": ") +
                              il.description}
                          </span>
                          <span>{renderCurrency(il.amount_with_vat)}</span>
                        </div>
                      ))}
                    </div>
                    <article>
                      <button
                        onclick={() => showingPopupS.set(true)}
                        style="width: 100%; display: block; height: var(--input-height);"
                      >
                        {"Pas aan"}
                      </button>
                    </article>
                  </div>
                );
              } else {
                return <span></span>;
              }
            })}
            {renderPopupModal(showingPopupS, ({ close }) => (
              <div
                className={style.panel}
                style="max-width: 1280px; border: 2px solid var(--grey-900);"
              >
                <div
                  className={style.panel_header}
                  style={`background: ${makeGradient(orange)}; color: ${
                    orange.text
                  };`}
                >
                  {renderClosePopupButton(close)}
                  <div>{"Factuurlijnen"}</div>
                </div>
                {arrayF.render()}
                <div style="text-align: right">
                  <button onclick={close}>
                    <i className={FA.icon_compress}></i>
                    {"Verklein"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        );
      },
    };
  } else {
    return arrayF;
  }

  function renderHeader() {
    const verticalAlignStyle = "vertical-align: bottom;";
    const amountThStyle =
      (renderSmall ? "" : "width: 124px;") + verticalAlignStyle;
    const selectboxThStyle =
      (renderSmall ? "" : "width: 148px;") + verticalAlignStyle;
    const trashThStyle =
      (renderSmall ? "width: 48px;" : "width: 66px;") + verticalAlignStyle;

    return (
      <tr>
        <th style={verticalAlignStyle}>{"Naam"}</th>
        <th style={amountThStyle}>{"Bedrag"}</th>
        {opts.country === "Netherlands" ? (
          <th style={amountThStyle}>{"Bedrag < 21y"}</th>
        ) : (
          []
        )}
        <th style={amountThStyle}>{"BTW %"}</th>
        {opts.allowedChargemodes && opts.allowedChargemodes.length === 1 ? (
          []
        ) : (
          <th style={selectboxThStyle}>
            {"Aanrekening"}{" "}
            {helpIcon({
              title: "Aanrekening",
              renderHelpContents: renderChargemodeHelp,
            })}
          </th>
        )}
        <th style={selectboxThStyle}>{"Type"}</th>
        <th style={trashThStyle}></th>
      </tr>
    );
  }

  function singleLineForm(
    initial: IL<PRICE>,
    opts: {
      allowedTypes?: invoicelinetype[];
      allowedChargemodes?: formulachargemode[];
      country: country;
    },
  ): Field<IL<PRICE>> {
    return new Form({})
      .addField("description", [], () =>
        textBox({
          initialVal: initial.description,
        }),
      )
      .addField("chargemode", [], () => {
        const options: formulachargemode[] = opts.allowedChargemodes || [
          "Enrollment",
          "Lesson",
        ];
        if (options.length === 1) {
          return constantField(options[0]);
        } else {
          return selectBox({
            values: options,
            initial: initial.chargemode,
            show: showFormulaChargeMode,
          });
        }
      })
      .addField("type_", [], () => {
        const options: invoicelinetype[] = opts.allowedTypes || [
          "Normal",
          "Discount",
          "Optional",
        ];
        return selectBox({
          values: options,
          initial: initial.type_,
          show: showInvoicelineType,
        });
      })
      .addField(
        "amount_with_vat_",
        [],
        () => currencyBox(initial.amount_with_vat, {}) as Promise<Field<PRICE>>,
      )
      .addField(
        "amount_with_vat",
        ["type_", "amount_with_vat_"],
        ({ type_, amount_with_vat_ }) =>
          constantField(
            type_ === "Normal" || type_ === "Optional"
              ? (absCurrency(amount_with_vat_) as PRICE)
              : (negateCurrency(absCurrency(amount_with_vat_)) as PRICE),
          ),
      )
      .addField(
        "amount_without_vat_",
        [],
        (): Promise<Field<PRICE | null>> =>
          opts.country === "Netherlands"
            ? (currencyBoxOptional(initial.amount_without_vat, {}) as Promise<
                Field<PRICE | null>
              >)
            : constantField(null),
      )
      .addField(
        "amount_without_vat",
        ["type_", "amount_without_vat_"],
        ({ type_, amount_without_vat_ }) =>
          constantField(
            amount_without_vat_ === null
              ? null
              : type_ === "Normal" || type_ === "Optional"
              ? (absCurrency(amount_without_vat_) as PRICE)
              : (negateCurrency(absCurrency(amount_without_vat_)) as PRICE),
          ),
      )
      .addField("vat", [], () => numberBox(initial.vat, {}))
      .build({
        render: (fields) => [
          <td>{fields.description()}</td>,
          <td>{fields.amount_with_vat_()}</td>,
          ...(opts.country === "Netherlands"
            ? [<td>{fields.amount_without_vat_()}</td>]
            : []),
          <td>{fields.vat()}</td>,
          <td>{fields.chargemode()}</td>,
          <td>{fields.type_()}</td>,
        ],
      });
  }
}

export function singleDueDateCalculationField(opts: {
  initialVal: singleDueDateCalculation;
  label: string | null;
}): Field<singleDueDateCalculation> {
  return new Form({})
    .addField("tag", [], () => {
      const options: singleDueDateCalculation["tag"][] = [
        "RelativeToFirstLesson",
        "RelativeToFirstDayOfPeriod",
        "RelativeToCreationDate",
      ];
      return selectBox({
        values: options,
        initial: opts.initialVal.tag,
        show: showSingleDueDateCalculationTag,
        label: "",
      });
    })
    .addField("days", [], () => {
      return numberBox(opts.initialVal.days);
    })
    .build({
      render: (fields) => (
        <div>
          {opts.label === null ? <span></span> : <label>{opts.label}</label>}
          <div style="display: flex;">
            <div style="flex: 1;">{fields.tag()}</div>
            <div style="width: 80px; margin-left: 8px;">{fields.days()}</div>
            <div
              className={style.input_like}
              style="flex: 0; margin-left: 8px;"
            >
              {"dagen"}
            </div>
          </div>
        </div>
      ),
    });
}

export function showSingleDueDateCalculationTag(
  n: singleDueDateCalculation["tag"],
): string {
  switch (n) {
    case "RelativeToFirstLesson":
      return "Eerste lesdag";
    case "RelativeToFirstDayOfPeriod":
      return "Eerste dag schooljaar";
    case "RelativeToCreationDate":
      return "Dag aanmaak inschrijving";
    default:
      return checkAllCasesHandled(n);
  }
}

export async function currencyBox(
  initialVal?: currency,
  opts?: {
    label?: string;
    constraint?: "positive" | "negative";
    constraintLabel?: string;
  },
): Promise<Field<currency>> {
  return wrapField(
    await numberBox(initialVal?.value, opts),
    (s) => currency(s),
    (c) => c.value,
  );
}

export async function currencyBoxOptional(
  initialVal: currency | null,
  opts?: {
    label?: string;
    constraint?: "positive" | "negative";
    constraintLabel?: string;
  },
): Promise<Field<currency | null>> {
  return wrapField(
    await numberBoxOptional(initialVal ? initialVal.value : null, opts),
    (s) => (s === null ? null : currency(s)),
    (c) => (c === null ? null : c.value),
  );
}

function renderChargemodeHelp() {
  return (
    <div>
      <article>
        <b>{showFormulaChargeMode("Lesson")}</b>

        {": " +
          "Het bedrag zal vermenigvuldigd worden met het aantal lessen in de lessenreeks."}
      </article>
      <article>
        <b>{showFormulaChargeMode("Enrollment")}</b>
        {": " +
          "Het bedrag wordt niet aangepast door de specifieke lessenreeks"}
      </article>
    </div>
  );
}
